import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, Loader2, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { deleteEnhancement, fmtTime, listEnhancements } from "@/lib/enhanceApi";
import { safeApiError } from "@/lib/safeErrorText";

const STATUS = { running: ["Working…", "text-amber-200 bg-amber-300/10"], done: ["Ready", "text-emerald-200 bg-emerald-400/10"], error: ["Stopped", "text-rose-200 bg-rose-400/10"] };

function Row({ it, onDelete }) {
  const [label, cls] = STATUS[it.status] || STATUS.running;
  return (
    <li data-testid={`enhance-history-${it.id}`} className="flex items-center gap-3 py-3">
      <Link to={`/create/enhance/${it.id}`} data-testid={`enhance-open-${it.id}`} className="group flex min-w-0 flex-1 items-center gap-3">
        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-[14px] font-semibold group-hover:text-amber-200">{it.title}</span>
          <span className="block truncate text-[11px] text-white/45">
            {new Date(it.created_at).toLocaleDateString()} · {fmtTime(it.info?.duration)}{it.key ? ` · ${it.key}` : ""}{it.isolate ? " · music removed" : ""}
          </span>
        </span>
        <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] ${cls}`}>{label}</span>
        <ChevronRight className="h-4 w-4 text-white/35 transition-transform group-hover:translate-x-0.5" />
      </Link>
      <button type="button" data-testid={`enhance-delete-${it.id}`} aria-label="Delete" onClick={() => onDelete(it)} disabled={it.status === "running"}
        className="grid h-9 w-9 place-items-center rounded-full text-white/40 transition-colors hover:bg-rose-500/15 hover:text-rose-200 disabled:opacity-30">
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
}

/** Every vocal you've enhanced — open to compare again, download or delete. */
export default function EnhanceHistory() {
  const [rows, setRows] = useState(null);
  const [doomed, setDoomed] = useState(null);
  const load = () => listEnhancements().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const remove = async () => {
    try { await deleteEnhancement(doomed.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Couldn't delete", { description: safeApiError(e) }); }
    finally { setDoomed(null); }
  };
  return (
    <section data-testid="enhance-history" className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5 sm:p-7">
      <h2 className="font-display text-base font-semibold md:text-lg">Your enhanced vocals</h2>
      {rows === null ? <Loader2 className="mt-4 h-5 w-5 animate-spin text-white/40" /> : rows.length === 0 ? (
        <p data-testid="enhance-history-empty" className="mt-3 text-[13px] text-white/50">Nothing yet — upload your first vocal. A great first test: “True Love Comes From Within”.</p>
      ) : (
        <ul className="mt-2 divide-y divide-white/[0.06]">{rows.map((it) => <Row key={it.id} it={it} onDelete={setDoomed} />)}</ul>
      )}
      <AlertDialog open={Boolean(doomed)} onOpenChange={(o) => !o && setDoomed(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0b0e1c] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{doomed?.title}”?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">The upload and all its versions are removed. Takes you already sent to the Recording Studio stay there.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="enhance-delete-cancel" className="border-white/15 bg-transparent text-white hover:bg-white/10">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="enhance-delete-confirm" onClick={remove} className="bg-rose-500 text-white hover:bg-rose-400">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
